import { Injectable } from '@angular/core';
import { JwtUtils } from '../@core/utils/jwt.utils';

@Injectable({
  providedIn: 'root'
})
export class SimpleAdminCheckService {

  constructor() {}

  /**
   * Simple admin check - checks multiple sources
   */
  isAdmin(): boolean {
    // Method 1: Check JWT token from session storage
    const sessionData = localStorage.getItem('session');
    let token: string | null = null;
    
    if (sessionData) {
      try {
        const session = JSON.parse(sessionData);
        token = session.access_token;
        console.log('Found access_token in session:', token ? 'exists' : 'missing');
      } catch (error) {
        console.error('Error parsing session data:', error);
      }
    }
    
    // Fallback: check localStorage directly
    if (!token) {
      token = localStorage.getItem('access_token');
    }
    
    if (token) {
      const decoded = JwtUtils.decodeToken(token);
      if (decoded) {
        console.log('JWT Token decoded:', decoded);
        
        // Check for admin permissions in token
        if (decoded.permissions && Array.isArray(decoded.permissions)) {
          const adminPermissions = ['is_admin', 'is_superuser', 'is_compadmin', 'admin_access', 'view_passwords', 'do_manage_users', 'do_manage_roles'];
          const hasAdminPermission = decoded.permissions.some((p: string) => adminPermissions.includes(p));
          if (hasAdminPermission) {
            console.log('Admin permission found in JWT token:', decoded.permissions);
            return true;
          }
        }

        // Check for admin role in token (role 1 is admin)
        if (decoded.role === 1) {
          console.log('Admin role found in JWT token:', decoded.role);
          return true;
        }

        // Check for specific admin user IDs
        if (decoded.uid === 1 || decoded.uid === 11 || decoded.userid === 1 || decoded.userid === 11) {
          console.log('Admin user ID found in JWT token:', decoded.uid || decoded.userid);
          return true;
        }
      }
    }

    // Method 2: Check localStorage for admin flags
    const userPermissions = localStorage.getItem('userPermissions');
    if (userPermissions) {
      try {
        const permissions = JSON.parse(userPermissions);
        console.log('User permissions from localStorage:', permissions);
        
        if (permissions.permissions && Array.isArray(permissions.permissions)) {
          const adminPermissions = ['is_admin', 'is_superuser', 'is_compadmin', 'admin_access', 'view_passwords'];
          const hasAdminPermission = permissions.permissions.some((p: string) => adminPermissions.includes(p));
          if (hasAdminPermission) {
            console.log('Admin permission found in localStorage');
            return true;
          }
        }
      } catch (error) {
        console.error('Error parsing userPermissions from localStorage:', error);
      }
    }

    // Method 3: Check for specific admin indicators
    const isAdminFlag = localStorage.getItem('isAdmin');
    if (isAdminFlag === 'true') {
      console.log('Admin flag found in localStorage');
      return true;
    }

    // Method 4: Check for specific user IDs (fallback)
    const userId = localStorage.getItem('userId') || localStorage.getItem('userid');
    if (userId && (userId === '1' || userId === '11')) {
      console.log('Admin user ID found in localStorage:', userId);
      return true;
    }

    console.log('No admin privileges found');
    return false;
  }

  /**
   * Debug method to show all available user data
   */
  debugUserData(): void {
    console.log('=== USER DATA DEBUG ===');
    
    // Session data
    const sessionData = localStorage.getItem('session');
    if (sessionData) {
      try {
        const session = JSON.parse(sessionData);
        console.log('Session data:', session);
        
        if (session.access_token) {
          const decoded = JwtUtils.decodeToken(session.access_token);
          console.log('JWT Token from session (decoded):', decoded);
        }
      } catch (error) {
        console.log('Session data (raw):', sessionData);
      }
    } else {
      console.log('No session data found');
    }
    
    // JWT Token (fallback)
    const token = localStorage.getItem('access_token');
    if (token) {
      const decoded = JwtUtils.decodeToken(token);
      console.log('JWT Token from localStorage (decoded):', decoded);
    } else {
      console.log('No JWT token in localStorage');
    }

    // User Permissions
    const userPermissions = localStorage.getItem('userPermissions');
    if (userPermissions) {
      try {
        console.log('User Permissions (localStorage):', JSON.parse(userPermissions));
      } catch (error) {
        console.log('User Permissions (raw):', userPermissions);
      }
    } else {
      console.log('No userPermissions in localStorage');
    }

    // All localStorage keys
    console.log('All localStorage keys:', Object.keys(localStorage));

    // Check admin status
    console.log('Is Admin:', this.isAdmin());
    console.log('=======================');
  }
}
